import React from "react";
import { useI18n } from "../i18n";

interface Plan {
  nameKey: string;
  price: string;
  myrPrice: string;
  periodKey: string;
  descKey: string;
  featureKeys: string[];
  ctaKey: string;
  highlight: boolean;
  badgeKey?: string;
}

const plans: Plan[] = [
  {
    nameKey: "pricing.free.name",
    price: "$0",
    myrPrice: "RM0",
    periodKey: "",
    descKey: "pricing.free.description",
    featureKeys: [
      "pricing.free.f1",
      "pricing.free.f2",
      "pricing.free.f3",
      "pricing.free.f4",
      "pricing.free.f5",
    ],
    ctaKey: "pricing.free.cta",
    highlight: false,
  },
  {
    nameKey: "pricing.starter.name",
    price: "$19.99",
    myrPrice: "~RM79",
    periodKey: "pricing.perMonth",
    descKey: "pricing.starter.description",
    featureKeys: [
      "pricing.starter.f1",
      "pricing.starter.f2",
      "pricing.starter.f3",
      "pricing.starter.f4",
      "pricing.starter.f5",
      "pricing.starter.f6",
      "pricing.starter.f7",
    ],
    ctaKey: "pricing.starter.cta",
    highlight: false,
  },
  {
    nameKey: "pricing.pro.name",
    price: "$49.99",
    myrPrice: "~RM197",
    periodKey: "pricing.perMonth",
    descKey: "pricing.pro.description",
    featureKeys: [
      "pricing.pro.f1",
      "pricing.pro.f2",
      "pricing.pro.f3",
      "pricing.pro.f4",
      "pricing.pro.f5",
      "pricing.pro.f6",
      "pricing.pro.f7",
      "pricing.pro.f8",
    ],
    ctaKey: "pricing.pro.cta",
    highlight: true,
    badgeKey: "pricing.pro.badge",
  },
  {
    nameKey: "pricing.business.name",
    price: "$99.99",
    myrPrice: "~RM394",
    periodKey: "pricing.perMonth",
    descKey: "pricing.business.description",
    featureKeys: [
      "pricing.business.f1",
      "pricing.business.f2",
      "pricing.business.f3",
      "pricing.business.f4",
      "pricing.business.f5",
      "pricing.business.f6",
      "pricing.business.f7",
      "pricing.business.f8",
    ],
    ctaKey: "pricing.business.cta",
    highlight: false,
  },
];

export function Pricing({ showTitle = true }: { showTitle?: boolean } = {}) {
  const { t } = useI18n();

  return (
    <section id="pricing" className="py-20 px-6">
      <div className="max-w-6xl mx-auto">
        {showTitle && (
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {t("pricing.title")}
            </h2>
            <p className="text-[#8a7a9a] text-lg max-w-2xl mx-auto">
              {t("pricing.subtitle")}
            </p>
          </div>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {plans.map((plan) => (
            <div
              key={plan.nameKey}
              className={`relative rounded-xl p-8 transition-all duration-300 ${
                plan.highlight
                  ? "bg-[#a078d0]/10 border-2 border-[#a078d0] onyx-glow"
                  : "onyx-card"
              }`}
            >
              {plan.highlight && plan.badgeKey && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                  <span className="bg-[#7b5ea7] text-white text-xs font-semibold px-3 py-1 rounded-full">
                    {t(plan.badgeKey)}
                  </span>
                </div>
              )}

              <h3 className="text-xl font-semibold text-white mb-2">
                {t(plan.nameKey)}
              </h3>
              <p className="text-sm text-[#8a7a9a] mb-6">{t(plan.descKey)}</p>

              <div className="mb-1">
                <span className="text-4xl font-bold text-white">
                  {plan.price}
                </span>
                {plan.periodKey && (
                  <span className="text-[#8a7a9a] text-sm">{t(plan.periodKey)}</span>
                )}
              </div>
              {plan.myrPrice && (
                <p className="text-sm text-[#a078d0] mb-6">
                  {plan.myrPrice}{plan.periodKey ? t(plan.periodKey) : ""}
                </p>
              )}

              <ul className="space-y-3 mb-8">
                {plan.featureKeys.map((fk) => (
                  <li
                    key={fk}
                    className="flex items-center gap-2 text-sm text-[#d0c0e8]"
                  >
                    <svg
                      className="w-4 h-4 text-[#c0a0e0] flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M4.5 12.75l6 6 9-13.5"
                      />
                    </svg>
                    {t(fk)}
                  </li>
                ))}
              </ul>

              <a
                href="/signup"
                className={`block text-center py-3 rounded-lg font-medium transition-colors ${
                  plan.highlight
                    ? "bg-[#7b5ea7] hover:bg-[#6a4f96] text-white"
                    : "bg-[#2a2040] hover:bg-[#352558] text-[#c0a0e0]"
                }`}
              >
                {t(plan.ctaKey)}
              </a>
            </div>
          ))}
        </div>

        {showTitle && (
          <div className="text-center mt-10">
            <a
              href="/pricing"
              className="text-sm text-[#a078d0] hover:text-[#c0a0e0] transition-colors"
            >
              {t("pricing.seeFullPricing")}
            </a>
          </div>
        )}
      </div>
    </section>
  );
}
